/**
 * What Android's back button does (docs/MOBILE-PLAN.md, M2), in order:
 *
 * 1. **Something is open over the page** — a dialog, a sheet, a menu, the
 *    palette. It is closed with the Escape every one of them already answers,
 *    so focus restore and cleanup run exactly as for a keyboard user. Escape is
 *    sent *only* when one is open: a focused field may answer Escape too, and
 *    back must never throw away an edit.
 * 2. **Anywhere but the home screen, with history behind it** — back a page.
 * 3. **The home screen** (or an inner page opened cold, with no history) —
 *    ask, then send the app to the background.
 *
 * Plain DOM and no platform import, so it runs and is tested in a browser;
 * only the wiring in `main.tsx` is native. Ported from Compass's
 * `services/back.ts`, keeping the plan's "asks before leaving" — which Compass
 * later dropped, because a registered handler suppresses Android's
 * predictive-back animation. Recorded as an open question in BUILDLOG.
 */

/** Everything that sits over the page and closes on Escape: Radix dialogs (the
 *  palette and every sheet are Radix dialogs), menus and select lists. */
const OVERLAY = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
  '[aria-modal="true"]',
].join(", ");

/** The screen back leaves the app from. */
const HOME = "/";

export function overlayOpen(): boolean {
  return document.querySelector(OVERLAY) !== null;
}

export function handleBack(
  pathname: string,
  canGoBack: boolean,
  goBack: () => void,
  leave: () => void,
  confirmLeave: () => boolean = () => window.confirm("Leave MyDeck?"),
): void {
  if (overlayOpen()) {
    // From the focused element, bubbling — the route a real key press takes,
    // so it reaches the document listener every Radix layer uses.
    const target = document.activeElement ?? document.body;
    target.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    return;
  }
  if (canGoBack && pathname !== HOME) {
    goBack();
    return;
  }
  if (confirmLeave()) leave();
}
